import { Button, Text, VStack } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { liftSuspend } from "../../api";
import { colors } from "../../styles";
import { ISuspend } from "../../types";

interface SuspendedProps {
    suspendList: ISuspend[];
}

const HOUR_MS = 60 * 60 * 1000;

function formatRemaining(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
        .map((value) => String(value).padStart(2, "0"))
        .join(":");
}

export default function Suspended(props: SuspendedProps) {

    const queryClient = useQueryClient();
    const suspendCount = props.suspendList.length;
    const isPermanent = suspendCount >= 3;

    const activeSuspend = useMemo(() => {
        return props.suspendList
            .filter((suspend) => !suspend.isLifted)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    }, [props.suspendList]);

    const banDurationMs = suspendCount === 1 ? 24 * HOUR_MS : 48 * HOUR_MS;

    const [remainingMs, setRemainingMs] = useState(0);

    useEffect(() => {
        if (isPermanent || !activeSuspend) return;

        const endsAt = new Date(activeSuspend.createdAt).getTime() + banDurationMs;

        const tick = () => {
            setRemainingMs(endsAt - Date.now());
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);

        return () => window.clearInterval(intervalId);
    }, [activeSuspend, banDurationMs, isPermanent]);

    const canLift = remainingMs <= 0;

    const liftSuspendMutation = useMutation({
        mutationFn: liftSuspend,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getMyProfileSuspended"] });
        },
    });

    if (isPermanent) {
        return (
            <VStack
                width={"100%"}
                minHeight={"100vh"}
                justifyContent={"center"}
                alignItems={"center"}
                padding={5}
                textAlign={"center"}
            >
                <Text fontSize={28} fontWeight={700} color={colors.black} letterSpacing={"-0.02em"}>
                    영구 정지된 프로필입니다
                </Text>
            </VStack>
        );
    }

    return (
        <VStack
            width={"100%"}
            minHeight={"100vh"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={8}
            padding={5}
            textAlign={"center"}
        >
            <VStack spacing={2}>
                <Text fontSize={18} fontWeight={600} color={colors.g1}>
                    {suspendCount}회 정지된 프로필입니다
                </Text>
                {activeSuspend?.reason && (
                    <Text fontSize={12} fontWeight={700} color={colors.g3}>
                        사유: {activeSuspend.reason}
                    </Text>
                )}
            </VStack>

            <VStack spacing={1}>
                <Text
                    fontSize={{ base: 48, md: 64 }}
                    fontWeight={400}
                    color={colors.black}
                    letterSpacing={"0.06em"}
                    lineHeight={1.1}
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                    {formatRemaining(remainingMs)}
                </Text>
            </VStack>

            <Button
                marginTop={4}
                width={"200px"}
                height={"52px"}
                borderRadius={"26px"}
                backgroundColor={colors.blue}
                color={colors.white}
                fontSize={16}
                fontWeight={600}
                isDisabled={!canLift || liftSuspendMutation.isPending}
                opacity={canLift ? 1 : 0.35}
                _hover={{ backgroundColor: colors.blue }}
                _disabled={{ opacity: 0.35, cursor: "not-allowed" }}
                isLoading={liftSuspendMutation.isPending}
                onClick={() => liftSuspendMutation.mutate()}
            >
                해제하기
            </Button>
        </VStack>
    );
}
